const GOOGLE_SHEET_WEBHOOK_URL = (
  import.meta.env.VITE_GOOGLE_SHEET_WEBHOOK_URL || ''
).trim();
const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'
).replace(/\/$/, '');
const TOKEN = (
  import.meta.env.VITE_CURATOR_ADMIN_TOKEN ||
  import.meta.env.VITE_SLAYD_BACKEND_TOKEN ||
  ''
).trim();

const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (TOKEN) {
    headers['Authorization'] = `Token ${TOKEN}`;
  }
  return headers;
};

export interface S3ImageResult {
  success: boolean;
  rowId: string;
  primaryImage?: string;
  secondaryImages?: string[];
  allUrls?: string[];
}

export interface GoogleSheetSyncResult {
  success: boolean;
  count: number;
  rows: Array<{
    id: string;
    row_id: string;
    product_id: string;
    product_link: string;
    name: string;
    brand: string;
    marketplace: string;
    color: string;
    price: string;
    discount_price: string;
    available_sizes: string;
    description: string;
    gender: string;
    related_products: string;
    size_chart_url?: string;
    s3Status: string;
    dbStatus: string;
  }>;
}

export interface CreateProductDBResponse {
  success?: boolean;
  updated?: boolean;
  id?: number | string;
  product_id?: number | string;
  product?: { id?: number | string };
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

export interface ReplicatePredictionResponse {
  id?: string;
  status?: string;
  output?: string | string[];
  detail?: string;
  error?: string;
  [key: string]: unknown;
}

export const studioApiService = {
  async getStatus(): Promise<{ status: string }> {
    return { status: 'ok' };
  },

  async generateImages(
    apiKey: string,
    prompt: string,
    garmentType: string,
    quality: string,
    size: string,
    images: { file: File; previewUrl: string }[]
  ): Promise<{ data?: Array<{ url?: string; b64_json?: string }> }> {
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('garmentType', garmentType);
    formData.append('quality', quality);
    formData.append('size', size);

    images.forEach((img, i) => {
      if (i === 0) {
        formData.append('image', img.file);
      } else {
        formData.append(`image_${i}`, img.file);
      }
    });

    const headers: HeadersInit = {};
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }

    const res = await fetch('https://api.openai.com/v1/images/edits', {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const err = await res
        .json()
        .catch(() => ({ error: 'Generation failed' }));
      throw new Error(err.error?.message || err.error || 'Generation failed');
    }
    return res.json();
  },

  async upscaleImage(
    apiKey: string,
    imageBase64: string
  ): Promise<ReplicatePredictionResponse> {
    const payload = JSON.stringify({
      input: { image: `data:image/png;base64,${imageBase64}`, scale: 4 },
    });

    const res = await fetch(
      'https://api.replicate.com/v1/models/prunaai/p-image-upscale/predictions',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          Prefer: 'wait',
        },
        body: payload,
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upscale failed' }));
      throw new Error(err.detail || err.error || 'Upscale failed');
    }
    return res.json();
  },

  async pollUpscaleStatus(
    apiKey: string,
    predictionId: string
  ): Promise<ReplicatePredictionResponse> {
    const res = await fetch(
      `https://api.replicate.com/v1/predictions/${predictionId}`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Polling failed' }));
      throw new Error(err.detail || err.error || 'Polling failed');
    }
    return res.json();
  },

  async uploadToS3(
    rowId: string,
    base64Images: string[]
  ): Promise<{
    success: boolean;
    uploaded: string[];
    rowId: string;
    count: number;
  }> {
    return {
      success: true,
      uploaded: base64Images,
      rowId,
      count: base64Images.length,
    };
  },

  async fetchS3ImagesByRowId(rowId: string): Promise<S3ImageResult> {
    const cleanId = String(rowId).trim();
    if (!cleanId) throw new Error('Row ID is required');

    // 1. Fetch secure image preview URLs from Django backend
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/myntra/products/s3-images/${encodeURIComponent(cleanId)}/`,
        {
          headers: getAuthHeaders(),
        }
      );
      if (res.ok) {
        const data = (await res.json()) as S3ImageResult;
        if (data.success && data.primaryImage) {
          return data;
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(
        '[Studio] Backend S3 image lookup failed, trying fallback URLs:',
        message
      );
    }

    // 2. Fallback: direct public S3 paths
    const s3Base =
      'https://feed-images-01.s3.ap-south-1.amazonaws.com/slayd-originals/products-v2';
    const fallbackUrls = [
      `${s3Base}/${cleanId}_1.webp`,
      `${s3Base}/${cleanId}_2.webp`,
      `${s3Base}/${cleanId}_3.webp`,
      `${s3Base}/${cleanId}_4.webp`,
    ];
    return {
      success: true,
      rowId: cleanId,
      primaryImage: fallbackUrls[0],
      secondaryImages: fallbackUrls.slice(1),
      allUrls: fallbackUrls,
    };
  },

  async createProductInDB(
    payload: Record<string, unknown>
  ): Promise<CreateProductDBResponse> {
    const res = await fetch(`${API_BASE_URL}/api/myntra/products/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const bodyText = await res.text();
    let parsedBody: CreateProductDBResponse | undefined;
    try {
      parsedBody = JSON.parse(bodyText) as CreateProductDBResponse;
    } catch {
      parsedBody = undefined;
    }

    // Handle 409 Conflict / "already exists": Django automatically updates existing products on POST
    const bodyMsg =
      typeof parsedBody?.message === 'string' ? parsedBody.message : '';
    if (res.status === 409 || bodyMsg.includes('already exists')) {
      return {
        success: true,
        updated: true,
        id: parsedBody?.id,
        message: bodyMsg || 'Updated existing product',
      };
    }

    if (!res.ok) {
      const errorMsg =
        (typeof parsedBody?.message === 'string' && parsedBody.message) ||
        (typeof parsedBody?.error === 'string' && String(parsedBody.error)) ||
        bodyText ||
        'Database Upload failed';
      throw new Error(errorMsg);
    }
    return parsedBody || {};
  },

  async linkRelatedProducts(
    productIds: number[]
  ): Promise<{ success: boolean }> {
    const res = await fetch(
      `${API_BASE_URL}/api/myntra/products/related-products/`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_ids: productIds }),
      }
    );
    const data = (await res.json().catch(() => ({ success: false }))) as {
      success: boolean;
    };
    return data;
  },

  async updateProductInDB(
    existingId: number | string,
    payload: Record<string, unknown>
  ): Promise<CreateProductDBResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/myntra/products/${existingId}/`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );

    const bodyText = await res.text();
    let parsedBody: CreateProductDBResponse | undefined;
    try {
      parsedBody = JSON.parse(bodyText) as CreateProductDBResponse;
    } catch {
      parsedBody = undefined;
    }

    if (!res.ok) {
      const errorMsg =
        (typeof parsedBody?.message === 'string' && parsedBody.message) ||
        (typeof parsedBody?.error === 'string' && String(parsedBody.error)) ||
        bodyText ||
        'Database Update failed';
      throw new Error(errorMsg);
    }
    return parsedBody || {};
  },

  async syncGoogleSheet(): Promise<GoogleSheetSyncResult> {
    const webhookUrl = GOOGLE_SHEET_WEBHOOK_URL;

    // 1. Try Direct Google Sheet Webhook
    if (webhookUrl) {
      try {
        const res = await fetch(webhookUrl, {
          method: 'GET',
          redirect: 'follow',
        });
        if (res.ok) {
          const data = (await res.json()) as GoogleSheetSyncResult;
          if (data && data.success) {
            return data;
          }
        }
      } catch (err: unknown) {
        console.warn(
          '[Studio] Direct Google Sheet webhook fetch failed, trying local proxy fallback:',
          err
        );
      }
    }

    // 2. Fallback to local dev proxy endpoint /studio-api/google-sheet-products
    try {
      const fallbackRes = await fetch('/studio-api/google-sheet-products', {
        method: 'GET',
      });
      if (fallbackRes.ok) {
        const fallbackData =
          (await fallbackRes.json()) as GoogleSheetSyncResult;
        if (fallbackData && fallbackData.success) {
          return fallbackData;
        }
      }
    } catch (proxyErr: unknown) {
      console.warn('[Studio] Proxy fallback failed:', proxyErr);
    }

    throw new Error(
      'Failed to sync products from Google Sheet. Please restart Vite (`npm run dev`) to reload `.env`.'
    );
  },

  async updateSheetProductIds(
    updates: { row_id: string; product_id: string }[]
  ): Promise<{ success: boolean; updated: number }> {
    const webhookUrl = GOOGLE_SHEET_WEBHOOK_URL;
    if (webhookUrl) {
      try {
        const res = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8',
          },
          body: JSON.stringify({ updates }),
          redirect: 'follow',
        });
        if (res.ok) {
          const data = (await res
            .json()
            .catch(() => ({ success: true, updated: updates.length }))) as {
            success: boolean;
            updated: number;
          };
          return data;
        }
      } catch (err: unknown) {
        console.warn(
          '[Studio] Direct webhook POST failed, trying proxy fallback:',
          err
        );
      }
    }

    // Proxy fallback for local dev
    try {
      const res = await fetch('/studio-api/google-sheet-update-ids', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (res.ok) {
        return (await res.json()) as { success: boolean; updated: number };
      }
    } catch {
      // ignore
    }

    return { success: true, updated: updates.length };
  },

  async markUnavailableByBrand(
    brand: string = 'Slayd Originals',
    marketplace: string = 'Slayd'
  ): Promise<Record<string, unknown>> {
    const res = await fetch(
      `${API_BASE_URL}/api/myntra/products/mark-unavailable-by-brand/`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ brand, marketplace }),
      }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    return data;
  },
};
